import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import api from "@/lib/api";
import BottomNav from "@/components/BottomNav";

export default function LevelPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = () => { setError(false); api.get("/users/me/level").then(({ data: d }) => setData(d)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  if (error && !data) return (
    <div className="min-h-screen max-w-md mx-auto flex flex-col items-center justify-center gap-3" data-testid="level-error">
      <p className="text-zinc-400 text-sm">Couldn't load level info</p>
      <button data-testid="level-retry" onClick={load} className="gradient-primary text-white text-xs font-bold px-5 py-2 rounded-full">Retry</button>
    </div>
  );
  if (!data) return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-[#E6B84C] border-t-transparent animate-spin" /></div>;

  const pct = Math.min(1, data.xp_into / data.xp_needed);
  const R = 62, C = 2 * Math.PI * R;

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-28 relative overflow-hidden" data-testid="level-page">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-44 rounded-full bg-[#8A2BE2]/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between mb-6">
        <button data-testid="level-back" onClick={() => navigate(-1)} className="text-[#E6B84C]"><ArrowLeft size={20} /></button>
        <h1 className="font-royal text-2xl gradient-gold-text font-bold tracking-wide">LEVEL {data.level}</h1>
        <span className="w-5" />
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center">
        <div className="relative w-40 h-40">
          <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
            <circle cx="70" cy="70" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
            <circle cx="70" cy="70" r={R} fill="none" stroke="url(#lg)" strokeWidth="7" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - pct)} style={{ filter: "drop-shadow(0 0 8px rgba(230,184,76,0.8))" }} />
            <defs><linearGradient id="lg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#8A2BE2" /><stop offset="100%" stopColor="#E6B84C" /></linearGradient></defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rotate-45 rounded-xl bg-gradient-to-br from-[#8A2BE2] to-[#FF007F] shadow-[0_0_24px_rgba(138,43,226,0.7)] flex items-center justify-center">
              <span className="-rotate-45 text-white font-extrabold text-2xl font-display" data-testid="level-number">{data.level}</span>
            </div>
          </div>
        </div>
        <p className="text-white font-bold text-sm mt-4" data-testid="level-xp">{data.xp_into.toLocaleString("en-IN")} / {data.xp_needed.toLocaleString("en-IN")} XP</p>
        <p className="text-zinc-500 text-[11px]">to Level {Math.min(100, data.level + 1)}</p>
      </motion.div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-7 mb-3">Level Perks</h2>
      <div className="glass rounded-2xl p-4 space-y-2.5" data-testid="level-perks">
        {data.perks.map((p) => (
          <p key={p} className="text-zinc-200 text-xs font-semibold">{p}</p>
        ))}
      </div>

      <h2 className="font-royal text-lg gradient-gold-text font-bold mt-7 mb-3">Badges</h2>
      <div className="grid grid-cols-4 gap-2.5" data-testid="badges-grid">
        {data.badges.map((b) => (
          <div key={b.key} data-testid={`badge-${b.key}`}
            className={`relative rounded-2xl p-3 text-center border ${b.unlocked ? "glass border-[#E6B84C]/40 shadow-[0_0_12px_rgba(230,184,76,0.15)]" : "bg-[#131A2A]/60 border-white/5"}`}
            style={{ clipPath: "polygon(50% 0%, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%)" }}>
            <p className={`text-2xl ${b.unlocked ? "" : "grayscale opacity-30"}`}>{b.emoji}</p>
            <p className={`text-[8px] font-bold mt-1 leading-tight ${b.unlocked ? "text-[#E6B84C]" : "text-zinc-600"}`}>{b.name}</p>
            {!b.unlocked && <Lock size={10} className="absolute top-2 right-1/2 translate-x-8 text-zinc-600" />}
          </div>
        ))}
      </div>

      <button data-testid="view-level-rewards" onClick={() => navigate("/vip")}
        className="mt-7 w-full bg-gradient-to-r from-[#FDE08B] via-[#FF9E4D] to-[#FF007F] text-white font-extrabold font-display py-4 rounded-full shadow-[0_0_24px_rgba(255,0,127,0.45)] active:scale-[0.98] transition-transform tracking-wide">
        VIEW LEVEL REWARDS
      </button>
      <BottomNav />
    </div>
  );
}
